import { useState, useRef, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import L from 'leaflet';
import { toast } from 'react-toastify';
import { geoSearchService } from '../../../../utils/nominatimGeocoding';
import { MAP_SEARCH_QUERY_MIN_LENGTH } from '../constants';

/**
 * Wyszukiwanie adresu na mapie (Nominatim) + okrąg zasięgu (Leaflet)
 */
export function useMapSearch(mapRef, onSearchComplete) {
  const { t } = useTranslation(['discover']);
  const [searchAddress, setSearchAddress] = useState('');
  const [searchRange, setSearchRange] = useState(5);
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchCenter, setSearchCenter] = useState(null);
  const [searchOpen, setSearchOpen] = useState(false);

  const searchAutocompleteTimeoutRef = useRef(null);
  const searchCircleRef = useRef(null);

  const handleAddressInput = useCallback(async (value) => {
    setSearchAddress(value);

    if (searchAutocompleteTimeoutRef.current) {
      clearTimeout(searchAutocompleteTimeoutRef.current);
    }

    if (value.length < MAP_SEARCH_QUERY_MIN_LENGTH) {
      setSearchSuggestions([]);
      return;
    }

    searchAutocompleteTimeoutRef.current = setTimeout(async () => {
      try {
        const result = await geoSearchService.autocomplete(value, {
          types: ['address', '(cities)'],
          componentRestrictions: { country: 'pl' },
        });
        setSearchSuggestions(result.predictions || []);
      } catch {
        setSearchSuggestions([]);
      }
    }, 300);
  }, []);

  const calculateZoomFromRange = useCallback((range) => {
    if (range >= 20) return 11;
    if (range >= 10) return 12;
    if (range >= 5) return 13;
    if (range >= 2) return 14;
    return 15;
  }, []);

  const handleSearch = useCallback(
    async (placeId = null, address = null, coords = null) => {
      const map = mapRef.current;
      if (!map) {
        return;
      }

      setSearching(true);
      if (searchAutocompleteTimeoutRef.current) {
        clearTimeout(searchAutocompleteTimeoutRef.current);
        searchAutocompleteTimeoutRef.current = null;
      }
      setSearchSuggestions([]);

      const textQuery = address != null ? String(address).trim() : '';
      const willUseFreeText = !coords && !placeId && textQuery.length > 0;

      if (willUseFreeText && textQuery.length < MAP_SEARCH_QUERY_MIN_LENGTH) {
        toast.warning(
          t('discover.map.searchQueryTooShort', {
            defaultValue: 'Wpisz co najmniej {{min}} znaków, aby wyszukać lokalizację.',
            min: MAP_SEARCH_QUERY_MIN_LENGTH,
          })
        );
        setSearching(false);
        return;
      }

      if (!coords && !placeId && !textQuery) {
        setSearching(false);
        return;
      }

      try {
        let location = coords;
        let attemptedFreeTextGeocode = false;

        if (!location && placeId) {
          const place = await geoSearchService.getPlaceDetails(placeId);
          if (place?.geometry?.location) {
            const lat =
              typeof place.geometry.location.lat === 'function'
                ? place.geometry.location.lat()
                : place.geometry.location.lat;
            const lng =
              typeof place.geometry.location.lng === 'function'
                ? place.geometry.location.lng()
                : place.geometry.location.lng;
            location = { lat, lng };
            setSearchAddress(place.formatted_address || address || '');
          }
        } else if (!location && address) {
          const trimmed = String(address).trim();
          if (trimmed.length >= MAP_SEARCH_QUERY_MIN_LENGTH) {
            attemptedFreeTextGeocode = true;
            const result = await geoSearchService.geocodeAddress(trimmed);
            if (result && result.lat != null && result.lng != null) {
              location = { lat: result.lat, lng: result.lng };
              setSearchAddress(result.address || trimmed);
            }
          }
        }

        if (!location && attemptedFreeTextGeocode) {
          toast.info(
            t('discover.map.searchGeocodeNoResult', {
              defaultValue: 'Nie znaleziono tej lokalizacji — doprecyzuj adres lub wybierz podpowiedź z listy.',
            })
          );
        }

        if (location) {
          setSearchCenter(location);
          const ll = L.latLng(location.lat, location.lng);
          map.panTo(ll);
          const zoom = calculateZoomFromRange(searchRange);
          map.setZoom(zoom);

          if (searchCircleRef.current) {
            map.removeLayer(searchCircleRef.current);
          }

          const circle = L.circle(ll, {
            radius: searchRange * 1000,
            color: '#EA6820',
            weight: 2,
            opacity: 0.85,
            fillColor: '#EA6820',
            fillOpacity: 0.15,
            interactive: false,
          }).addTo(map);

          searchCircleRef.current = circle;

          setTimeout(() => {
            if (onSearchComplete) {
              onSearchComplete();
            }
          }, 500);
        }
      } catch (err) {
        console.error('Search error:', err);
      } finally {
        setSearching(false);
        setSearchSuggestions([]);
      }
    },
    [mapRef, searchRange, calculateZoomFromRange, onSearchComplete, t]
  );

  const handleSuggestionClick = useCallback(
    (prediction) => {
      setSearchAddress(prediction.description || '');
      if (prediction.lat != null && prediction.lon != null) {
        handleSearch(null, null, { lat: prediction.lat, lng: prediction.lon });
      } else {
        handleSearch(prediction.osm_ids || prediction.place_id, null);
      }
    },
    [handleSearch]
  );

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !searchCenter || !searchCircleRef.current) {
      return;
    }

    searchCircleRef.current.setRadius(searchRange * 1000);
    const zoom = calculateZoomFromRange(searchRange);
    map.setZoom(zoom);

    setTimeout(() => {
      if (onSearchComplete) {
        onSearchComplete();
      }
    }, 300);
  }, [searchRange, searchCenter, mapRef, calculateZoomFromRange, onSearchComplete]);

  const closeSearch = useCallback(() => {
    setSearchOpen(false);
    const map = mapRef.current;
    if (searchCircleRef.current && map) {
      map.removeLayer(searchCircleRef.current);
      searchCircleRef.current = null;
    }
    setSearchCenter(null);
  }, [mapRef]);

  /** Usuwa okrąg wyszukiwania i środek — np. z chipa „promień” (bez zamykania panelu). */
  const clearSearchLocation = useCallback(() => {
    const map = mapRef.current;
    if (searchCircleRef.current && map) {
      map.removeLayer(searchCircleRef.current);
      searchCircleRef.current = null;
    }
    setSearchCenter(null);
    setSearchAddress('');
    setSearchSuggestions([]);
    setTimeout(() => {
      if (onSearchComplete) {
        onSearchComplete();
      }
    }, 0);
  }, [mapRef, onSearchComplete]);

  const cleanup = useCallback(() => {
    const map = mapRef.current;
    if (searchCircleRef.current && map) {
      map.removeLayer(searchCircleRef.current);
      searchCircleRef.current = null;
    }
    if (searchAutocompleteTimeoutRef.current) {
      clearTimeout(searchAutocompleteTimeoutRef.current);
    }
  }, [mapRef]);

  return {
    searchAddress,
    searchRange,
    searchSuggestions,
    searching,
    searchCenter,
    searchOpen,
    setSearchAddress,
    setSearchRange,
    setSearchOpen,
    handleAddressInput,
    handleSearch,
    handleSuggestionClick,
    closeSearch,
    clearSearchLocation,
    cleanup,
  };
}

export default useMapSearch;
